import { useEffect, useMemo, useState } from 'react';
import { Icon, type IconName } from '../components/Icon.tsx';
import { ReceiptDialog } from '../components/Receipt.tsx';
import { RefundDialog } from '../components/Refund.tsx';
import { Shell } from '../components/Shell.tsx';
import { EmptyState, Skeleton, StatusBadge } from '../components/ui.tsx';
import { t, tn } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { clock, money } from '../lib/format.ts';
import type { PayMethod } from '../lib/types.ts';
import { useUi } from '../lib/ui-state.tsx';
import { useLinkParam } from '../lib/link-params.ts';

interface Row {
  id: string;
  number: number;
  name: string;
  state: 'paid' | 'cancelled';
  kind: 'sale' | 'refund';
  refund_reason: string | null;
  refund_of_no: number | null;
  /** Given back so far on a sale. */
  refunded: number;
  total: number;
  tip: number;
  receipt_no: number | null;
  closed_at: string;
  cancel_reason: string | null;
  closed_by_name: string | null;
  methods: PayMethod[];
  prints: number;
}

const METHOD_ICON: Record<PayMethod, IconName> = { cash: 'cash', card: 'card', other: 'receipt', gift_card: 'gift', points: 'star' };

/** "2026-10-24" in this device's own calendar. */
const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const dayRange = (day: string) => {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return { from: new Date(y, m - 1, d).toISOString(), to: new Date(y, m - 1, d + 1).toISOString() };
};

/** Today's paid and cancelled bills, to find one and print it again. */
export function Receipts() {
  const { fail } = useUi();
  const today = isoDay(new Date());
  const yesterday = isoDay(new Date(Date.now() - 86_400_000));
  const [day, setDay] = useState(today);
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<Row[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  // The search opens one receipt: /receipts?open=<order id>.
  useLinkParam('open', setOpen);
  const [refunding, setRefunding] = useState<string | null>(null);
  const [printNow, setPrintNow] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => setRows(null), [day]);
  useEffect(() => {
    const timer = setTimeout(
      () => void action<Row[]>('sales.receipts', { ...dayRange(day), search: search.trim() || undefined }).then(setRows, fail),
      search ? 250 : 0,
    );
    return () => clearTimeout(timer);
  }, [day, search, fail, version]);

  const paid = useMemo(() => (rows ?? []).filter((r) => r.state === 'paid' && r.kind === 'sale'), [rows]);
  const refunds = useMemo(() => (rows ?? []).filter((r) => r.state === 'paid' && r.kind === 'refund'), [rows]);
  // Takings are net: money given back today comes off.
  const takings = [...paid, ...refunds].reduce((s, r) => s + r.total + r.tip, 0);
  const refundedTotal = -refunds.reduce((s, r) => s + r.total, 0);

  return (
    <Shell>
      <div className="page">
        <div className="page-head">
          <div>
            <h1>{t('receipts.title')}</h1>
            <p className="muted">
              {rows
                ? refunds.length
                  ? t('receipts.summary_refunds', { n: paid.length, total: money(takings), refunded: money(refundedTotal) })
                  : t('receipts.summary', { n: paid.length, total: money(takings) })
                : t('receipts.subtitle')}
            </p>
          </div>
        </div>
        <div className="toolbar">
          <div className="toolbar-group">
            <div className="segmented" role="radiogroup" aria-label={t('receipts.day')}>
              {[
                [today, t('receipts.today')],
                [yesterday, t('receipts.yesterday')],
              ].map(([d, label]) => (
                <button key={d} role="radio" aria-checked={day === d} className={day === d ? 'active' : ''} onClick={() => setDay(d!)}>
                  {label}
                </button>
              ))}
            </div>
            <input
              className="input date-input"
              type="date"
              aria-label={t('receipts.day')}
              value={day}
              max={today}
              onChange={(e) => e.target.value && setDay(e.target.value)}
            />
          </div>
          <div className="search grow">
            <Icon name="search" size={18} />
            <input
              className="input"
              type="search"
              placeholder={t('receipts.search')}
              aria-label={t('receipts.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {!rows ? (
          <Skeleton h={320} r="var(--radius-lg)" />
        ) : rows.length === 0 ? (
          <EmptyState icon="receipt" title={search ? t('order.no_results') : t('receipts.empty')} body={search ? undefined : t('receipts.empty_body')} />
        ) : (
          <div className="list-card" role="list">
            {rows.map((r) =>
              r.state === 'paid' && r.kind === 'refund' ? (
                <button key={r.id} role="listitem" className="list-row refund-row" onClick={() => setOpen(r.id)}>
                  <span className="list-icon receipt-no" aria-hidden="true">
                    {r.receipt_no}
                  </span>
                  <span className="list-main">
                    <span className="list-title">{t('receipts.refund_title', { n: r.receipt_no ?? '', name: r.name })}</span>
                    <span className="list-sub">
                      {clock(r.closed_at)}
                      {` · ${t('receipts.refund_of', { n: r.refund_of_no ?? '' })}`}
                      {r.refund_reason ? ` · ${r.refund_reason}` : ''}
                    </span>
                  </span>
                  <span className="list-price">−{money(-r.total)}</span>
                  <StatusBadge tone="warn" icon="undo" label={t('receipts.refund')} />
                </button>
              ) : r.state === 'paid' ? (
                <button key={r.id} role="listitem" className="list-row" onClick={() => setOpen(r.id)}>
                  <span className="list-icon receipt-no" aria-hidden="true">
                    {r.receipt_no}
                  </span>
                  <span className="list-main">
                    <span className="list-title">{t('receipts.row_title', { n: r.receipt_no ?? '', name: r.name })}</span>
                    <span className="list-sub">
                      {clock(r.closed_at)}
                      {r.closed_by_name ? ` · ${r.closed_by_name}` : ''}
                      {r.prints ? ` · ${tn('receipts.printed', r.prints)}` : ''}
                    </span>
                  </span>
                  <span className="receipt-methods" role="img" aria-label={r.methods.map((m) => t(`pay.method_${m}`)).join(', ')} title={r.methods.map((m) => t(`pay.method_${m}`)).join(', ')}>
                    {r.methods.map((m) => (
                      <Icon key={m} name={METHOD_ICON[m]} size={18} />
                    ))}
                  </span>
                  <span className="list-price">{money(r.total + r.tip)}</span>
                  {r.refunded > 0 ? (
                    <StatusBadge
                      tone="warn"
                      icon="undo"
                      label={r.refunded >= r.total ? t('receipts.refunded') : t('receipts.part_refunded', { amount: money(r.refunded) })}
                    />
                  ) : (
                    <StatusBadge tone="ok" icon="check" label={t('receipts.paid')} />
                  )}
                </button>
              ) : (
                <div key={r.id} role="listitem" className="list-row archived">
                  <span className="list-icon" aria-hidden="true">
                    <Icon name="x" size={18} />
                  </span>
                  <span className="list-main">
                    <span className="list-title">{t('receipts.cancelled_title', { n: r.number, name: r.name })}</span>
                    <span className="list-sub">
                      {clock(r.closed_at)}
                      {r.cancel_reason ? ` · ${r.cancel_reason}` : ''}
                    </span>
                  </span>
                  <StatusBadge tone="neutral" icon="x" label={t('order.cancelled')} />
                </div>
              ),
            )}
          </div>
        )}
      </div>
      {open && !refunding ? (
        <ReceiptDialog
          key={open}
          order={open}
          printNow={printNow}
          onClose={() => {
            setOpen(null);
            setPrintNow(false);
            setVersion((v) => v + 1);
          }}
          onRefund={() => setRefunding(open)}
        />
      ) : null}
      {refunding ? (
        <RefundDialog
          order={refunding}
          onClose={() => setRefunding(null)}
          onDone={(refund) => {
            // Straight to the refund receipt, printing: the guest signs or keeps it.
            setRefunding(null);
            setPrintNow(true);
            setOpen(refund);
            setVersion((v) => v + 1);
          }}
        />
      ) : null}
    </Shell>
  );
}
